import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type { MarketListingStatus, MarketListingSummary } from '@/types/market';
import { formatPrice, timeAgo } from './MarketShared';
import styles from './Market.module.scss';

interface EditState {
  title: string;
  description: string;
  price: string;
  city: string;
  area: string;
  isNegotiable: boolean;
}

export default function MarketMyListings() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<MarketListingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const mounted = useRef(true);

  const load = async () => {
    setLoading(true);
    try {
      const items = await apiService.mktGetMyListings();
      if (!mounted.current) return;
      setListings(items);
      setError(null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load your listings.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, []);

  const setStatus = async (id: string, status: MarketListingStatus) => {
    setBusyId(id);
    try {
      await apiService.mktSetListingStatus(id, status);
      if (mounted.current) setListings((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Could not update status.');
    } finally {
      if (mounted.current) setBusyId(null);
    }
  };

  const startEdit = (l: MarketListingSummary) => {
    setEditingId(l.id);
    setEdit({
      title: l.title || '',
      description: '',
      price: l.price != null ? String(l.price) : '',
      city: l.city || '',
      area: '',
      isNegotiable: false,
    });
  };

  const saveEdit = async (id: string) => {
    if (!edit) return;
    setBusyId(id);
    setError(null);
    try {
      const priceNum = edit.price.trim() === '' ? null : Number(edit.price);
      if (priceNum !== null && Number.isNaN(priceNum)) {
        setError('Price must be a number.');
        setBusyId(null);
        return;
      }
      const updated = await apiService.mktUpdateListing(id, {
        title: edit.title.trim(),
        description: edit.description.trim() || undefined,
        price: priceNum,
        city: edit.city.trim() || undefined,
        area: edit.area.trim() || undefined,
        isNegotiable: edit.isNegotiable,
      });
      if (!mounted.current) return;
      setListings((prev) => prev.map((l) => (l.id === id
        ? { ...l, title: updated.title, price: updated.price, city: updated.city ?? l.city }
        : l)));
      setEditingId(null);
      setEdit(null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to save changes.');
    } finally {
      if (mounted.current) setBusyId(null);
    }
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate('/customer/market')}>← Marketplace</button>
          <span className={styles.spacer} />
          <button type="button" className={styles.primaryBtn} onClick={() => navigate('/customer/market/post')}>+ Post an ad</button>
        </div>
        <h1 className={styles.title}>My listings</h1>

        {error && <p className={styles.error}>{error}</p>}
        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}

        {!loading && listings.length === 0 && !error && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>📋</div>
            You haven’t posted anything yet.
          </div>
        )}

        {!loading && listings.length > 0 && (
          <div className={styles.threadList}>
            {listings.map((l) => {
              const busy = busyId === l.id;
              const status = l.status || 'active';
              const isEditing = editingId === l.id && edit;
              return (
                <div key={l.id} className={styles.panel} style={{ margin: 0 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {l.thumbnail ? (
                      <img className={styles.thumb} style={{ width: 72, height: 72 }} src={l.thumbnail} alt={l.title} />
                    ) : (
                      <div className={styles.cardThumbPlaceholder} style={{ width: 72, height: 72, aspectRatio: 'auto', borderRadius: 10, fontSize: 24 }}>
                        {l.kind === 'service' ? '🛠️' : '📦'}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={styles.cardPrice}>{formatPrice(l.price, l.currency)}</div>
                      <div className={styles.threadTitle}>{l.title}</div>
                      <div className={styles.cardMeta} style={{ justifyContent: 'flex-start', gap: 10 }}>
                        <span className={`${styles.badge} ${status === 'active' ? styles.badgeActive : styles.badgeSold}`}>{status}</span>
                        <span>{timeAgo(l.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {isEditing ? (
                    <div style={{ marginTop: 12 }}>
                      <input className={styles.input} value={edit!.title} onChange={(e) => setEdit({ ...edit!, title: e.target.value })}
                        placeholder="Title" style={{ marginBottom: 8 }} />
                      <textarea className={styles.textarea} value={edit!.description} onChange={(e) => setEdit({ ...edit!, description: e.target.value })}
                        placeholder="Description" style={{ minHeight: 70, marginBottom: 8 }} />
                      <div className={styles.searchRow}>
                        <input className={styles.input} type="number" value={edit!.price} onChange={(e) => setEdit({ ...edit!, price: e.target.value })}
                          placeholder="Price (blank = Contact for price)" style={{ flex: 1 }} />
                      </div>
                      <div className={styles.searchRow} style={{ marginTop: 8 }}>
                        <input className={styles.input} value={edit!.city} onChange={(e) => setEdit({ ...edit!, city: e.target.value })} placeholder="City" style={{ flex: 1 }} />
                        <input className={styles.input} value={edit!.area} onChange={(e) => setEdit({ ...edit!, area: e.target.value })} placeholder="Area" style={{ flex: 1 }} />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, color: 'var(--text-secondary)' }}>
                        <input type="checkbox" checked={edit!.isNegotiable} onChange={(e) => setEdit({ ...edit!, isNegotiable: e.target.checked })} />
                        Negotiable
                      </label>
                      <div className={styles.statusControls}>
                        <button type="button" className={styles.primaryBtn} onClick={() => saveEdit(l.id)} disabled={busy}>
                          {busy ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={() => { setEditingId(null); setEdit(null); }} disabled={busy}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.statusControls}>
                      <button type="button" className={styles.secondaryBtn} onClick={() => navigate(`/customer/market/listing/${l.id}`)}>View</button>
                      <button type="button" className={styles.secondaryBtn} onClick={() => startEdit(l)} disabled={busy}>Edit</button>
                      {status !== 'sold' && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => setStatus(l.id, 'sold')} disabled={busy}>Mark sold</button>
                      )}
                      {status !== 'closed' && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => setStatus(l.id, 'closed')} disabled={busy}>Close</button>
                      )}
                      {status !== 'active' && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => setStatus(l.id, 'active')} disabled={busy}>Reactivate</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
